import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  ArrowUp,
  ArrowDown,
  CheckCircle,
  XCircle,
  Eye,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import CompanyDetailsModal from '@/components/company/CompanyDetailsModal';

interface SignupRow {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  user_type: string | null;
  fmcsa_verified: boolean | null;
  fmcsa_mc_number: string | null;
  fmcsa_dot_number: string | null;
  fmcsa_legal_name: string | null;
  onboarding_completed: boolean | null;
  email_verified: boolean | null;
  phone_verified: boolean | null;
  created_at: string;
  company_id: string | null;
  company_role: string | null;
  company_name: string | null;
  company_type: string | null;
  company_verification_status: string | null;
  documents_verified_at: string | null;
  company_mc_number: string | null;
  company_dot_number: string | null;
  sub_status: string | null;
  sub_billing_mode: string | null;
  sub_plan: string | null;
  sub_billing_cycle: string | null;
  sub_current_period_end: string | null;
  sub_trial_end_date: string | null;
  tier: 'paid' | 'free' | 'inactive' | 'none';
}

type SortKey =
  | 'created_at'
  | 'email'
  | 'user_type'
  | 'tier'
  | 'company_name'
  | 'fmcsa_verified';
type SortDir = 'asc' | 'desc';

const fmtDate = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const tierBadge = (tier: SignupRow['tier']) => {
  const map: Record<SignupRow['tier'], { label: string; cls: string }> = {
    paid: { label: 'Paid', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    free: { label: 'Free', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    inactive: { label: 'Inactive', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    none: { label: 'No sub', cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  };
  const v = map[tier];
  return <Badge variant="outline" className={`${v.cls} text-xs`}>{v.label}</Badge>;
};

export default function AdminSignups() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [modalCompanyId, setModalCompanyId] = useState<string | null>(null);
  const [modalCompanyName, setModalCompanyName] = useState<string>('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-signups-v'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_signups_v' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data as unknown as SignupRow[]) || [];
    },
  });

  const rows = data || [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tierFilter !== 'all' && r.tier !== tierFilter) return false;
      if (roleFilter !== 'all' && (r.user_type || '') !== roleFilter) return false;
      if (verifiedFilter === 'yes' && !r.fmcsa_verified) return false;
      if (verifiedFilter === 'no' && r.fmcsa_verified) return false;
      if (dateFrom && new Date(r.created_at) < new Date(dateFrom)) return false;
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(r.created_at) > end) return false;
      }
      if (term) {
        const hay = [
          r.email,
          r.first_name,
          r.last_name,
          r.phone,
          r.company_name,
          r.company_mc_number,
          r.company_dot_number,
          r.fmcsa_mc_number,
          r.fmcsa_dot_number,
          r.fmcsa_legal_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, search, tierFilter, roleFilter, verifiedFilter, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ k, label }: { k: SortKey; label: string }) => (
    <button
      onClick={() => toggleSort(k)}
      className="flex items-center gap-1 text-xs uppercase tracking-wide text-[#6b7280] hover:text-white transition-colors"
    >
      {label}
      {sortKey === k && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  );

  const handleVerifyFmcsa = async (row: SignupRow) => {
    if (row.fmcsa_verified) return;
    if (!confirm(`Manually mark FMCSA verified for ${row.email}?`)) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ fmcsa_verified: true })
        .eq('id', row.user_id);
      if (error) throw error;
      toast.success('FMCSA marked verified');
      qc.invalidateQueries({ queryKey: ['admin-signups-v'] });
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to mark verified');
    }
  };

  const handleVerifyShipper = async (row: SignupRow) => {
    if (!row.company_id) {
      toast.info('No company linked to this user');
      return;
    }
    if (row.documents_verified_at) return;
    if (!confirm(`Mark shipper company "${row.company_name || row.email}" as verified? This unlocks posting.`)) return;
    try {
      const { error } = await supabase
        .from('companies')
        .update({ documents_verified_at: new Date().toISOString() })
        .eq('id', row.company_id);
      if (error) throw error;
      toast.success('Shipper company marked verified');
      qc.invalidateQueries({ queryKey: ['admin-signups-v'] });
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to mark verified');
    }
  };

  const openCompany = (row: SignupRow) => {
    if (!row.company_id) {
      toast.info('No company linked to this user');
      return;
    }
    setModalCompanyId(row.company_id);
    setModalCompanyName(row.company_name || '');
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const paid = rows.filter((r) => r.tier === 'paid').length;
    const free = rows.filter((r) => r.tier === 'free').length;
    const verified = rows.filter((r) => r.fmcsa_verified).length;
    return { total, paid, free, verified };
  }, [rows]);

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Signups</h1>
            <p className="text-[#6b7280] mt-1 text-sm">
              Chronological feed of every new signup, with plan, role, and FMCSA status.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="bg-[#111820] border-[#1e2530] text-white hover:bg-[#1a2332]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', val: stats.total },
            { label: 'Paid', val: stats.paid },
            { label: 'Free', val: stats.free },
            { label: 'FMCSA verified', val: stats.verified },
          ].map((s) => (
            <Card key={s.label} className="bg-[#111820] border-[#1e2530] p-4">
              <div className="text-xs text-[#6b7280] uppercase tracking-wide">{s.label}</div>
              <div className="text-2xl font-semibold text-white mt-1">{s.val}</div>
            </Card>
          ))}
        </div>

        {/* Filter bar */}
        <Card className="bg-[#111820] border-[#1e2530] p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
              <Input
                placeholder="Search email, name, company, MC/DOT…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#0d1117] border-[#1e2530] text-white h-9"
              />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[130px] h-9 bg-[#0d1117] border-[#1e2530] text-white">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tiers</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="none">No sub</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[140px] h-9 bg-[#0d1117] border-[#1e2530] text-white">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="broker">Broker</SelectItem>
                <SelectItem value="carrier">Carrier</SelectItem>
                <SelectItem value="shipper">Shipper</SelectItem>
                <SelectItem value="broker_carrier">Broker + Carrier</SelectItem>
              </SelectContent>
            </Select>
            <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
              <SelectTrigger className="w-[150px] h-9 bg-[#0d1117] border-[#1e2530] text-white">
                <SelectValue placeholder="FMCSA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All verification</SelectItem>
                <SelectItem value="yes">FMCSA verified</SelectItem>
                <SelectItem value="no">Not verified</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[150px] h-9 bg-[#0d1117] border-[#1e2530] text-white"
              aria-label="From date"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[150px] h-9 bg-[#0d1117] border-[#1e2530] text-white"
              aria-label="To date"
            />
            {(search || tierFilter !== 'all' || roleFilter !== 'all' || verifiedFilter !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setTierFilter('all');
                  setRoleFilter('all');
                  setVerifiedFilter('all');
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-xs text-[#6b7280] hover:text-white"
              >
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-[#111820] border-[#1e2530] overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#14b8a6]" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="py-16 text-center text-[#6b7280] text-sm">
              No signups match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1e2530] hover:bg-transparent">
                    <TableHead><SortHeader k="created_at" label="Signed up" /></TableHead>
                    <TableHead><SortHeader k="email" label="User" /></TableHead>
                    <TableHead><SortHeader k="user_type" label="Role" /></TableHead>
                    <TableHead><SortHeader k="company_name" label="Company" /></TableHead>
                    <TableHead><SortHeader k="tier" label="Tier" /></TableHead>
                    <TableHead><SortHeader k="fmcsa_verified" label="FMCSA" /></TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wide text-[#6b7280]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((r) => (
                    <TableRow key={r.user_id} className="border-[#1e2530] hover:bg-[#1a2332]">
                      <TableCell className="text-xs text-[#9ca3af] whitespace-nowrap">
                        {fmtDate(r.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="text-white text-sm font-medium">
                          {(r.first_name || r.last_name)
                            ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                            : '—'}
                        </div>
                        <div className="text-xs text-[#6b7280]">{r.email}</div>
                        {r.phone && <div className="text-xs text-[#6b7280]">{r.phone}</div>}
                      </TableCell>
                      <TableCell>
                        {r.user_type ? (
                          <Badge variant="outline" className="text-xs capitalize text-[#9ca3af] border-[#2a3441]">
                            {r.user_type.replace('_', ' + ')}
                          </Badge>
                        ) : (
                          <span className="text-xs text-[#6b7280]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.company_name ? (
                          <button
                            onClick={() => openCompany(r)}
                            className="text-sm text-white hover:text-[#14b8a6] text-left"
                          >
                            {r.company_name}
                          </button>
                        ) : (
                          <span className="text-xs text-[#6b7280]">No company</span>
                        )}
                        {(r.company_mc_number || r.company_dot_number) && (
                          <div className="text-xs text-[#6b7280] mt-0.5">
                            {r.company_mc_number && <span>MC {r.company_mc_number}</span>}
                            {r.company_mc_number && r.company_dot_number && <span className="mx-1">·</span>}
                            {r.company_dot_number && <span>DOT {r.company_dot_number}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {tierBadge(r.tier)}
                          {r.sub_plan && (
                            <span className="text-[10px] text-[#6b7280] capitalize">
                              {r.sub_plan}{r.sub_billing_cycle ? ` · ${r.sub_billing_cycle}` : ''}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.fmcsa_verified ? (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-[#6b7280] border-[#2a3441]">
                            <XCircle className="w-3 h-3 mr-1" />
                            Not verified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-[#9ca3af] hover:text-white"
                            onClick={() => openCompany(r)}
                            disabled={!r.company_id}
                            title="View company details"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                          {r.user_type === 'shipper' ? (
                            !r.documents_verified_at && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300"
                                onClick={() => handleVerifyShipper(r)}
                                disabled={!r.company_id}
                                title="Mark shipper company verified (unlocks posting)"
                              >
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Verify Shipper
                              </Button>
                            )
                          ) : (
                            !r.fmcsa_verified && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300"
                                onClick={() => handleVerifyFmcsa(r)}
                                title="Manually mark FMCSA verified"
                              >
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Verify
                              </Button>
                            )
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <div className="text-xs text-[#6b7280] mt-3">
          Showing {sorted.length} of {rows.length} signups
        </div>
      </div>

      <CompanyDetailsModal
        isOpen={!!modalCompanyId}
        onClose={() => setModalCompanyId(null)}
        companyId={modalCompanyId}
        companyName={modalCompanyName}
      />
    </AdminLayout>
  );
}
